import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, Users, Mail, ExternalLink, BadgeCheck, Activity } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CCY_SYMBOL = { GBP: '£', USD: '$', INR: '₹', CAD: 'C$', EUR: '€' };
const fmt = (ccy, n) => `${CCY_SYMBOL[ccy] || ccy + ' '}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/**
 * AdminAutomatedConnections — Sovereign Matchmaker revenue widget.
 * Placed inside the admin dashboard. Pulls /api/admin/automated-connections.
 */
const AdminAutomatedConnections = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchData = async (d = days) => {
    setLoading(true);
    setErr(null);
    const token = localStorage.getItem('token');
    try {
      const r = await axios.get(`${API_URL}/api/admin/automated-connections?days=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runClaimInvites = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API_URL}/api/admin/claim-invites/run-now?batch_size=25`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Run failed');
    }
  };

  return (
    <div className="luxury-card p-6" data-testid="admin-automated-connections">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <span className="text-[10px] tracking-[0.4em] text-[#E07A3A] uppercase font-semibold">Sovereign Matchmaker</span>
          <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Automated Connections — Revenue</h3>
          <p className="text-xs text-slate-500 mt-1">All autonomous revenue streams across Matchmaking, Pulse, Boost Pack and Verified Partner subscriptions.</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => { setDays(d); fetchData(d); }}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${days === d ? 'bg-[#1F3D2B] text-white border-[#1F3D2B]' : 'border-slate-200 text-slate-500 hover:border-[#E07A3A] hover:text-[#E07A3A]'}`}
              data-testid={`connections-range-${d}`}>
              {d}d
            </button>
          ))}
          <button onClick={runClaimInvites} className="ml-2 px-3 py-1.5 text-xs uppercase tracking-widest bg-[#E07A3A] text-white hover:bg-[#C4652E]" data-testid="run-claim-invites-btn">
            Run claim-invites
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
      ) : err ? (
        <div className="text-sm text-rose-600">{err}</div>
      ) : data && (
        <>
          {/* Top-line stat strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <StatTile icon={<BadgeCheck className="h-5 w-5" />} label="Verified Partners" value={data.active_verified_partners} sub="active" />
            <StatTile icon={<Activity className="h-5 w-5" />} label="Pulse Subscribers" value={data.active_pulse_subscribers} sub="active" />
            <StatTile icon={<Mail className="h-5 w-5" />} label="Claim Invites" value={data.claim_invites_sent} sub={`last ${data.days}d`} />
            <StatTile icon={<ExternalLink className="h-5 w-5" />} label="Outbound Clicks" value={data.outbound_clicks} sub={`last ${data.days}d`} />
          </div>

          {/* Revenue breakdown */}
          <div className="bg-slate-50 border border-slate-100 rounded p-4 mb-4">
            <h4 className="text-xs tracking-widest text-slate-500 uppercase mb-2 flex items-center gap-2"><TrendingUp className="h-3 w-3" /> Revenue by stream</h4>
            <div className="space-y-2">
              {(data.buckets || []).map((b) => (
                <div key={b.key} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-slate-700">{b.label}</span>
                  <span className="text-slate-500 text-xs flex flex-wrap gap-2">
                    {b.by_currency.length === 0 ? '—' : b.by_currency.map((r) => (
                      <span key={r.currency} className="inline-flex items-center gap-1">
                        <strong className="text-slate-800">{fmt(r.currency, r.total)}</strong>
                        <span className="text-slate-400">({r.count})</span>
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Grand totals */}
          {data.grand_total_by_currency && Object.keys(data.grand_total_by_currency).length > 0 && (
            <div className="bg-[#1F3D2B] text-white p-4 rounded flex flex-wrap gap-4 items-center justify-between">
              <span className="text-[10px] tracking-[0.4em] uppercase text-[#F0A875]">Grand total ({data.days}d)</span>
              <div className="flex gap-4 text-lg font-['Playfair_Display']">
                {Object.entries(data.grand_total_by_currency).map(([ccy, v]) => (
                  <span key={ccy} data-testid={`grand-total-${ccy}`}>{fmt(ccy, v)}</span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StatTile = ({ icon, label, value, sub }) => (
  <div className="bg-white border border-slate-200 rounded p-3">
    <div className="flex items-center gap-2 text-slate-500">
      {icon}<span className="text-[10px] uppercase tracking-widest">{label}</span>
    </div>
    <div className="text-2xl font-['Playfair_Display'] text-[#1A1A1A] mt-1">{value ?? 0}</div>
    <div className="text-[11px] text-slate-400">{sub}</div>
  </div>
);

export default AdminAutomatedConnections;
